import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  CloudSun,
  Brain,
  ShieldAlert,
  Shirt,
  User,
  Bell,
  Map,
  Compass,
  Sun,
  BellRing,
  Info,
  Waves,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
}

interface NavGroup {
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { href: "/", label: "Dashboard", icon: LayoutDashboard },
      { href: "/forecast", label: "Forecast", icon: CloudSun },
      { href: "/map", label: "Map", icon: Map },
    ],
  },
  {
    items: [
      { href: "/insights", label: "Insights", icon: Brain },
      { href: "/alerts", label: "Alerts", icon: Bell },
    ],
  },
  {
    items: [
      { href: "/preparedness", label: "Preparedness", icon: ShieldAlert },
      { href: "/clothing", label: "Clothing", icon: Shirt },
      { href: "/compare", label: "Compare", icon: Compass },
      { href: "/coastal", label: "Coastal", icon: Waves },
    ],
  },
  {
    items: [
      { href: "/astronomy", label: "Astronomy", icon: Sun },
      { href: "/notifications", label: "Notifications", icon: BellRing },
      { href: "/account", label: "Account", icon: User },
      { href: "/about", label: "About", icon: Info },
    ],
  },
];

export function SidebarNav({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const rrLocation = useLocation();
  const pathname = rrLocation.pathname;

  return (
    <nav className="flex flex-col gap-1">
      {navGroups.map((group, gi) => (
        <div key={gi}>
          {gi > 0 && !collapsed && (
            <div className="mx-4 my-2 h-px bg-gradient-to-r from-border/40 via-border/10 to-transparent" />
          )}
          <div className={cn("flex flex-col gap-0.5", collapsed ? "px-2" : "px-2")}>
            {group.items.map((item) => {
              const isActive =
                item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onNavigate}
                  title={collapsed ? item.label : undefined}
                  className={cn(
                    "relative flex items-center rounded-xl transition-all duration-300 text-sm font-medium group/nav",
                    collapsed
                      ? cn(
                          "justify-center h-10 w-10 mx-auto",
                          isActive
                            ? "bg-primary/15 text-primary"
                            : "text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                        )
                      : cn(
                          "gap-3 px-3 py-2.5",
                          isActive
                            ? "bg-gradient-to-r from-primary/12 to-primary/5 text-primary shadow-sm"
                            : "text-muted-foreground/80 hover:bg-muted/30 hover:text-foreground"
                        )
                  )}
                >
                  {isActive && !collapsed && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-gradient-to-b from-primary via-primary/70 to-primary/30" />
                  )}
                  <item.icon
                    className={cn(
                      "flex-shrink-0 transition-all duration-300",
                      collapsed ? "h-5 w-5" : "h-[18px] w-[18px]",
                      isActive && collapsed && "drop-shadow-[0_0_8px_var(--color-primary)]",
                      isActive && !collapsed && "drop-shadow-[0_0_6px_var(--color-primary)]"
                    )}
                  />
                  {!collapsed && (
                    <span className="flex-1 truncate">{item.label}</span>
                  )}
                  {isActive && !collapsed && (
                    <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
